import { onMounted, watch } from "vue"
import { consumePendingAction, type PendingAction, pendingAction, pendingSearchOpen } from "../stores/uiStore"

/**
 * Subscribe a view to the global action bus (actions dispatched from the
 * command palette). Handles both timings transparently: an action dispatched
 * before the view mounted (checked on mount, when the DOM is ready for
 * focus/scroll work) and one dispatched while it is visible (watcher).
 */
export function useGlobalActions(handlers: Record<string, (action: PendingAction) => void>) {
  function tryConsume(value: PendingAction | null) {
    if (!value) return
    const handler = handlers[value.action]
    if (!handler) return
    const consumed = consumePendingAction(value.action)
    if (consumed) handler(consumed)
  }

  onMounted(() => tryConsume(pendingAction.value))
  watch(pendingAction, tryConsume)
}

/**
 * Subscribe to the global "open search" request (the header search button).
 * Same dual timing as useGlobalActions: fires on mount if a request is already
 * pending, and again for requests made while the view is visible.
 */
export function useSearchOpenRequest(onOpen: () => void) {
  function tryConsume(requested: boolean) {
    if (!requested) return
    pendingSearchOpen.value = false
    onOpen()
  }

  onMounted(() => tryConsume(pendingSearchOpen.value))
  watch(pendingSearchOpen, tryConsume)
}
